import { Navigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import LoadingPage from "../../public/LoadingPage";
import { CustomButton } from "../../atoms/CustomButton";
import UnknownProcessTypeView from "../error/UnknownProcessTypeView";
import { useLmcUserView } from "../../../contexts/AuthContext";
import { useGetProcessData } from "../../../hooks/entity";
import { mapProcessDataToBusinessCardPayload } from "../../../utils/processData";
import { useLanguageDetection } from "../../../hooks/useLanguageDetection";
import {
  generateBusinessCardHelmet,
  generateLoyaltyProgramHelmet,
  generateCustomRegistrationHelmet,
  generateEventHelmet,
} from "../../../utils/helmetUtils";

const ProcessViewApp = () => {
  const { t } = useTranslation("clientView");
  const { processId } = useParams<{ processId: string }>();
  const { search } = useLocation();
  const { dispatch } = useLmcUserView();

  const shouldFetch = Boolean(processId);

  const {
    data: processData,
    isLoading,
    isError,
    isSuccess,
    refetch,
  } = useGetProcessData(processId ?? "", {
    enabled: shouldFetch,
    onSuccess: (data) => {
      dispatch({ type: "ENTITY", payload: data });
    },
  });

  useLanguageDetection(processData);

  const processType = processData?.process_type;

  if (!shouldFetch) {
    return <UnknownProcessTypeView processType={undefined} />;
  }

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isError) {
    return <UnknownProcessTypeView processType={undefined} />;
  }

  if (!isSuccess || !processType) {
    return <UnknownProcessTypeView processType={processType} />;
  }

  switch (processType) {
    case "business_card_form": {
      if (!processData) {
        return (
          <div className="flex flex-col items-center justify-center min-h-screen p-7">
            <h4 className="mb-4 text-center">
              {t("error.businessCard.loadFailed")}
            </h4>
            <CustomButton
              onClick={() => refetch()}
              title={t("lmcUser.key2") as string}
            />
          </div>
        );
      }

      const businessCardPayload =
        mapProcessDataToBusinessCardPayload(processData);

      if (!businessCardPayload) {
        return (
          <div className="flex flex-col items-center justify-center min-h-screen p-7">
            <h4 className="mb-4 text-center">
              {t("error.businessCard.incomplete")}
            </h4>
            <CustomButton
              onClick={() => refetch()}
              title={t("lmcUser.key2") as string}
            />
          </div>
        );
      }

      const { userData, businessCardData, entityData } = businessCardPayload;

      return (
        <>
          {generateBusinessCardHelmet({
            userData,
            businessCardData,
            entityData,
            processId,
          })}
          <Navigate
            to={`/business-card/${processId}${search}`}
            replace
            state={{
              from: "process",
              processData: processData,
            }}
          />
        </>
      );
    }

    case "lmc_form": {
      if (!processData) {
        return <UnknownProcessTypeView processType={processType} />;
      }

      return (
        <>
          {generateLoyaltyProgramHelmet({ processData, processId })}
          <Navigate
            to={`/loyalty-program/${processId}${search}`}
            replace
            state={{
              from: "process",
              processData: processData,
            }}
          />
        </>
      );
    }

    case "custom_registration": {
      if (!processData) {
        return <UnknownProcessTypeView processType={processType} />;
      }

      return (
        <>
          {generateCustomRegistrationHelmet({ processData, processId })}
          <Navigate
            to={`/custom-registration/${processId}${search}`}
            replace
            state={{
              from: "process",
              processData: processData,
            }}
          />
        </>
      );
    }

    case "events_config": {
      if (!processData) {
        return <UnknownProcessTypeView processType={processType} />;
      }

      return (
        <>
          {generateEventHelmet({ processData, processId })}
          <Navigate
            to={`/event/${processId}${search}`}
            replace
            state={{
              from: "process",
              processData: processData,
            }}
          />
        </>
      );
    }

    default:
      return <UnknownProcessTypeView processType={processType} />;
  }
};

export default ProcessViewApp;
